import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import { consumeAuthFrom } from '../utils/authRedirect.ts';
import './css/RegistrationPage.css';

function translateApiMessage(t: (key: string) => string, msg: string | undefined): string {
  if (!msg) return t('verifyEmail.serverError');
  const map: Record<string, string> = {
    'verifyEmail.invalidToken': t('verifyEmail.invalidOrExpired'),
    'verifyEmail.invalidOrExpiredToken': t('verifyEmail.invalidOrExpired'),
    'verifyEmail.changeSuccess': t('verifyEmail.changeSuccess'),
    'verifyEmail.serverError': t('verifyEmail.serverError'),
    'verifyEmail.emailRequired': t('verifyEmail.emailRequired'),
    'verifyEmail.resendCooldown': t('verifyEmail.resendCooldown'),
    'registration.emailSendFailed': t('registration.emailSendFailed'),
    'registration.emailNotConfigured': t('registration.emailNotConfigured'),
    'registration.emailAlreadyTaken': t('registration.emailAlreadyTaken'),
  };
  return map[msg] || msg;
}

export default function VerifyEmailPage() {
  const { t, currentLanguage } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [changedEmail, setChangedEmail] = useState(false);
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    document.title = t('verifyEmail.title');
  }, [t]);

  useEffect(() => {
    if (!token) {
      setStatus('err');
      setError(t('verifyEmail.invalidOrExpired'));
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setError('');

    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/registration/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        let data = await r.json().catch(() => ({}));
        let emailChanged = false;

        if (!r.ok && r.status === 400) {
          const changeRes = await fetch(`${API_BASE}/api/profile/email/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token }),
          });
          data = await changeRes.json().catch(() => ({}));
          if (cancelled) return;
          if (!changeRes.ok) {
            setStatus('err');
            setError(translateApiMessage(t, data.message));
            return;
          }
          emailChanged = Boolean(data.emailChanged);
        } else if (!r.ok) {
          if (cancelled) return;
          setStatus('err');
          setError(translateApiMessage(t, data.message));
          return;
        }

        if (cancelled) return;
        setChangedEmail(emailChanged);
        setStatus('ok');
        if (data.user) {
          login({
            id: String(data.user.id),
            username: data.user.username,
            email: data.user.email,
            role: data.user.role || 'user',
          });
        }
        setTimeout(() => navigate(emailChanged ? '/settings' : consumeAuthFrom(), { replace: true }), 800);
      } catch {
        if (!cancelled) {
          setStatus('err');
          setError(t('verifyEmail.serverError'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только token из URL
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendStatus('');
    if (!resendEmail.trim()) {
      setResendStatus(t('verifyEmail.emailRequired'));
      return;
    }
    setResendLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/registration/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail.trim(), lang: currentLanguage }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 429) {
        setResendStatus(t('verifyEmail.resendCooldown'));
      } else if (!r.ok) {
        setResendStatus(translateApiMessage(t, data.message));
      } else {
        let line = t('verifyEmail.resendSent');
        if (data.devVerificationLink && typeof data.devVerificationLink === 'string') {
          line = `${t('registration.devLinkHint')} ${data.devVerificationLink}`;
        }
        setResendStatus(line);
      }
    } catch {
      setResendStatus(t('verifyEmail.serverError'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="RegistrationPage">
        <h1>{t('verifyEmail.title')}</h1>

        {status === 'loading' && <p className="error-message" style={{ color: '#888' }}>{t('verifyEmail.verifying')}</p>}
        {status === 'ok' && (
          <p style={{ color: '#28a745' }}>
            {changedEmail ? t('verifyEmail.changeSuccess') : t('verifyEmail.success')}
          </p>
        )}
        {status === 'err' && error && <div className="error-message">{error}</div>}

        {(status === 'err' || !token) && (
          <div style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{t('verifyEmail.resendTitle')}</h2>
            <p style={{ marginBottom: '1rem', color: '#aaa', fontSize: '0.95rem' }}>{t('verifyEmail.resendHint')}</p>
            <form onSubmit={handleResend}>
              <div className="form-group">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder={t('verifyEmail.emailPlaceholder')}
                  required
                  style={{ width: '100%', padding: '10px', marginBottom: 8 }}
                />
              </div>
              <button type="submit" className="submit-btn" disabled={resendLoading}>
                {resendLoading ? t('registration.resendSending') : t('verifyEmail.resendButton')}
              </button>
            </form>
            {resendStatus && <p style={{ marginTop: '1rem', fontSize: '0.9rem', wordBreak: 'break-word' }}>{resendStatus}</p>}
          </div>
        )}

        <div className="login-redirect" style={{ marginTop: '1.5rem' }}>
          <Link to="/login" className="login-link">{t('verifyEmail.goToLogin')}</Link>
        </div>
      </div>
    </div>
  );
}
